import { Node, Edge } from "@xyflow/react"
import type {
  NodeConditionCheck,
  NodeConditionCheckAnd,
  NodeConditionCheckOr
} from "./workflowConditions"

export type ExecutionStatus = "completed" | "failed" | "skipped" | "pending"

/**
 * Injected UI-only fields used to render the inline "+" button on
 * terminal (unconnected) source handles and execution status overlays.
 * These are populated by the canvas and read by each node component —
 * never persisted.
 */
export interface NodeDisplayExtras {
  _terminalHandles?: string[]
  _onAddAtTerminal?: (nodeId: string, handleId: string) => void
  /** Handle ID of the terminal "+" currently being hovered by a dragged
   *  node. Used by the nodes to render a highlighted drop-target state. */
  _activeTerminalDropHandle?: string
  /** Per-run execution status of this node, set by the ExecutionCanvas
   *  when replaying a workflow execution. Undefined in the editor. */
  _executionStatus?: ExecutionStatus
  /** Error message from a failed execution — rendered below the node body. */
  _failedMessage?: string
  /** Inline "Add case" handler injected by the canvas onto BRANCH nodes. */
  _onAddCase?: (nodeId: string) => void
}

export interface TriggerNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "TRIGGER"
  label: string
  triggerType: string
}

export const CHECK_KIND_LEAF = "leaf" as const
export const CHECK_KIND_GROUP = "group" as const

export const OPERATOR_AND = "AND" as const
export const OPERATOR_OR = "OR" as const

export type CheckGroupOperator = typeof OPERATOR_AND | typeof OPERATOR_OR

/** A single (non-compound) backend condition check. */
export type LeafCheck = Exclude<
  NodeConditionCheck,
  NodeConditionCheckAnd | NodeConditionCheckOr
>

/** Editor-state leaf — wraps the canonical backend check shape directly so
 *  the converter can pass it straight through without re-derivation. */
export interface CheckLeaf {
  kind: typeof CHECK_KIND_LEAF
  check: LeafCheck
}

/** Editor-state group — a list of children combined with AND/OR. */
export interface CheckGroup {
  kind: typeof CHECK_KIND_GROUP
  operator: CheckGroupOperator
  children: CheckExpression[]
}

export type CheckExpression = CheckLeaf | CheckGroup

export interface ConditionNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "CONDITION"
  label: string
  /** The full check tree for this condition node — leaf for a single
   *  backend check, group for AND/OR-combined checks. */
  expression: CheckExpression
}

export interface ActionNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "ACTION"
  label: string
  actionType: string
  reason?: string
  reasonDescription?: string
  customState?: string
  deliveryGiftCardProducts?: boolean
}

/** A single case on a BRANCH node. The `id` is a stable client UUID used
 *  as the suffix in the source handle id (`branch-case-<id>`) so reordering
 *  cases doesn't break already-attached edges. */
export interface BranchCase {
  id: string
  label: string
  expression: CheckExpression
}

export interface BranchNodeData extends Record<string, unknown>, NodeDisplayExtras {
  nodeType: "BRANCH"
  label: string
  /** Ordered list of cases. Always length ≥ 2 in the editor. The implicit
   *  `otherwise` fallback is rendered as the rightmost outcome but is not
   *  stored here — its handle id is HANDLE_BRANCH_OTHERWISE. */
  cases: BranchCase[]
}

export type WorkflowNodeData =
  | TriggerNodeData
  | ConditionNodeData
  | ActionNodeData
  | BranchNodeData

export type WorkflowNode = Node<WorkflowNodeData>
export type WorkflowEdge = Edge
